import axios, { AxiosProgressEvent, AxiosResponse } from 'axios'

import { gcsAxios } from '@/api'

export type UploadProgressCallback = (percent: number) => void

const DEFAULT_MAX_RETRIES = 20
const DEFAULT_CHUNK_SIZE = 5 * 1024 * 1024 // 5 MiB
const BASE_BACKOFF_MS = 50
const BACKOFF_MULTIPLIER = 1.5

/**
 * Sleep for the given time unless the signal is aborted.
 */
function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const id = setTimeout(resolve, ms)

    if (signal.aborted) {
      clearTimeout(id)
      reject(new DOMException('Aborted', 'AbortError'))
    }

    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(id)
        reject(new DOMException('Aborted', 'AbortError'))
      },
      { once: true },
    )
  })
}

/**
 * Extract the last byte index from a GCS Range header.
 * Example header: "bytes=0-1048575"
 */
function parseRangeHeader(rangeHeader: string): number | null {
  const match = /bytes=(\d+)-(\d+)/.exec(rangeHeader)
  return match ? parseInt(match[2], 10) : null
}

/**
 * Calculate exponential backoff with jitter.
 */
function calcBackoffMs(attempt: number): number {
  const jitter = Math.random() + 0.5 // [0.5, 1.5)
  return BASE_BACKOFF_MS * BACKOFF_MULTIPLIER ** (attempt - 1) * jitter
}

function makeProgressReporter(fileSize: number, cb?: UploadProgressCallback) {
  let last = -1

  return (baseOffset: number, loaded: number): void => {
    const raw = ((baseOffset + loaded) / fileSize) * 100
    const percent = Math.min(100, Math.floor(raw))

    if (percent > last) {
      last = percent
      cb?.(percent)
    }
  }
}

/**
 * Upload a file to GCS using a resumable upload URL, retrying failed chunks
 * with exponential back‑off and supporting cancellation via AbortSignal.
 */
export async function uploadFileWithRetry(
  file: File,
  resumableUrl: string,
  signal: AbortSignal,
  onUploadProgress?: UploadProgressCallback,
  maxRetries = DEFAULT_MAX_RETRIES,
  chunkSize = DEFAULT_CHUNK_SIZE,
): Promise<void> {
  let offset = 0

  while (offset < file.size) {
    if (signal.aborted) throw new Error('Upload aborted by user')

    const chunkEndExclusive = Math.min(offset + chunkSize, file.size)
    const chunk = file.slice(offset, chunkEndExclusive)

    let attempt = 0
    let uploaded = false

    const report = makeProgressReporter(file.size, onUploadProgress)

    while (!uploaded && attempt < maxRetries) {
      attempt += 1
      const offsetAtRequestStart = offset

      try {
        const response: AxiosResponse = await gcsAxios.put(
          resumableUrl,
          chunk,
          {
            headers: {
              'Content-Type': file.type || 'application/octet-stream',
              'Content-Range': `bytes ${offset}-${chunkEndExclusive - 1}/${file.size}`,
            },
            validateStatus: (status) =>
              (status >= 200 && status < 300) || status === 308,
            timeout: 120_000,
            signal,
            onUploadProgress: (e: AxiosProgressEvent) => {
              if (typeof e.loaded === 'number')
                report(offsetAtRequestStart, e.loaded)
            },
          },
        )

        // Successful or partially successful response
        if (response.status === 308) {
          // Partial accept – update offset from Range header (if present)
          const lastByte = response.headers.range
            ? parseRangeHeader(response.headers.range)
            : chunkEndExclusive - 1

          offset = (lastByte ?? chunkEndExclusive - 1) + 1
        } else {
          // 2xx – chunk (and maybe whole file) fully accepted
          offset = chunkEndExclusive
        }

        onUploadProgress?.(Math.round((offset / file.size) * 100))
        uploaded = true
      } catch (err) {
        if (
          axios.isCancel(err) ||
          (err instanceof DOMException && err.name === 'AbortError')
        ) {
          throw new Error('Upload aborted by user')
        }

        if (attempt >= maxRetries) throw err

        await sleep(calcBackoffMs(attempt), signal)
      }
    }
  }
}
